import { useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { DocMeta } from '../content/docs';
import MermaidDiagram from './MermaidDiagram';

// react-markdown component overrides: render ```mermaid fences as live diagrams,
// leave all other code blocks as normal fenced code.
function classNames(node: unknown): string[] {
  const cls = (node as { properties?: { className?: unknown } })?.properties?.className;
  return Array.isArray(cls) ? cls.map(String) : [];
}
const MD_COMPONENTS = {
  code({ className, children }: { className?: string; children?: React.ReactNode }) {
    if (/\blanguage-mermaid\b/.test(className ?? '')) {
      return <MermaidDiagram chart={String(children)} />;
    }
    return <code className={className}>{children}</code>;
  },
  pre({ node, children }: { node?: unknown; children?: React.ReactNode }) {
    const child = (node as { children?: unknown[] })?.children?.[0];
    if (classNames(child).some((c) => c.includes('language-mermaid'))) {
      // The code override already rendered the diagram; drop the <pre> wrapper.
      return <>{children}</>;
    }
    return <pre>{children}</pre>;
  },
};

/**
 * Renders a doc according to its kind:
 *   md   — GitHub-flavoured markdown (react-markdown + remark-gfm).
 *   svg  — standalone vector image, inlined so it inherits page width/theme.
 *   html — a self-contained "design file" (HTML + CSS + inline SVG) rendered
 *          in a sandboxed iframe so its styles are isolated from the app shell.
 *          The iframe carries no `allow-scripts`, so the document is static.
 *
 * SVG/HTML docs are authored by the platform team and reviewed via PR before
 * they reach the content blob, so inlining their markup is within the same
 * trust boundary as the rest of the site's committed source.
 */
export default function DocBody({ doc }: { doc: DocMeta }) {
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Self-contained HTML is same-origin (srcDoc + allow-same-origin), so we can
  // size the iframe to its content on load and keep it in sync if it reflows.
  const sizeFrame = useCallback(() => {
    const frame = frameRef.current;
    const body = frame?.contentDocument?.body;
    const root = frame?.contentDocument?.documentElement;
    if (!frame || !body || !root) return;
    const height = Math.max(body.scrollHeight, root.scrollHeight);
    frame.style.height = `${height + 24}px`;
  }, []);

  if (doc.kind === 'html') {
    return (
      <iframe
        ref={frameRef}
        className="doc-html-frame"
        title={doc.title}
        srcDoc={doc.content}
        sandbox="allow-same-origin allow-popups"
        onLoad={sizeFrame}
      />
    );
  }

  if (doc.kind === 'svg') {
    return (
      <div
        className="doc-svg"
        role="img"
        aria-label={doc.title}
        // eslint-disable-next-line react/no-danger -- trusted, PR-reviewed asset
        dangerouslySetInnerHTML={{ __html: doc.content }}
      />
    );
  }

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
      {doc.content}
    </ReactMarkdown>
  );
}
